import { Box, Flex, IconButton, Tooltip, Icon } from "@chakra-ui/react";
import { useState, useCallback, useRef, useEffect } from "react";
import { BsStars, BsX } from "react-icons/bs";
import { Objects } from "./Objects";
import { ChatPanel } from "../../components/ChatDrawer/chat";

export const ObjectsLayout = () => {
  const [leftWidth, setLeftWidth] = useState(75); // Percentage
  const [isDragging, setIsDragging] = useState(false);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const newLeftWidth = (mouseX / containerRect.width) * 100;

      // Constrain between 20% and 80% to ensure both panels remain usable
      const constrainedWidth = Math.min(Math.max(newLeftWidth, 20), 80);
      setLeftWidth(constrainedWidth);
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <Flex
      ref={containerRef}
      h="88.5vh"
      w="100%"
      overflow="hidden"
      position="relative"
    >
      {/* Left Panel - Objects */}
      <Box
        width={isAiAssistantOpen ? `${leftWidth}%` : "100%"}
        overflowY="auto"
        bg="transparent"
        p={4}
        transition={isDragging ? "none" : "width 0.3s ease"}
        position="relative"
        zIndex="1"
      >
        <Objects />
      </Box>
      {isAiAssistantOpen && (
        <Box
          width="4px"
          bg="transparent"
          cursor="col-resize"
          position="relative"
          onMouseDown={handleMouseDown}
          _hover={{
            bg: "transparent",
          }}
          _active={{
            bg: "transparent",
          }}
        />
      )}
      {/* Right Panel - Chat */}
      <Box
        flex={isAiAssistantOpen ? "1" : "0"}
        display="flex"
        minWidth={isAiAssistantOpen ? "300px" : "0"}
        maxWidth={isAiAssistantOpen ? "none" : "0"}
        overflow="hidden"
        transition="all 0.3s ease"
        // p={isAiAssistantOpen ? 4 : 0}
        my={isAiAssistantOpen ? 4 : 0}
      >
        {isAiAssistantOpen && <ChatPanel topSpacing={0} />}
      </Box>

      {/* AI Assistant Toggle Button */}
      <Box position="absolute" top="2" right="0" zIndex="10">
        <Tooltip
          label={isAiAssistantOpen ? "Hide AI Assistant" : "Show AI Assistant"}
          placement="left"
        >
          <IconButton
            aria-label={
              isAiAssistantOpen ? "Hide AI Assistant" : "Show AI Assistant"
            }
            icon={
              <Icon
                as={
                  isAiAssistantOpen
                    ? (BsX as React.ElementType)
                    : (BsStars as React.ElementType)
                }
                boxSize={4}
              />
            }
            onClick={() => setIsAiAssistantOpen(!isAiAssistantOpen)}
            bg="rgba(118, 82, 247, 0.8)"
            color="white"
            size="sm"
            borderRadius="full"
            _hover={{
              bg: "rgba(118, 82, 247, 1)",
              transform: "scale(1.1)",
            }}
            _active={{
              bg: "rgba(118, 82, 247, 0.9)",
            }}
            transition="all 0.2s ease"
          />
        </Tooltip>
      </Box>
    </Flex>
  );
};
